import React, { useMemo } from 'react';
import { HelpCircle, Info } from 'lucide-react';
import {
    PFLICHT_ART_LABEL,
    pflichtStandAus,
    pflichtenStand,
    schwellenwerteAusMerkmalen,
} from '../planungLogic';
import { AusgeloestZaehler, PflichtZeile } from './GrundlagenBausteine';
import type { GrundlagenProps } from './typen';

/** Abschnitt „Prüfung nach Baustellenverordnung" — das Ergebnis der Pruefung:
 *  ausgeloeste Fragen, Pflichten, Anhang II, Stand der Festschreibung. */
export const GrundlagenPruefung: React.FC<GrundlagenProps> = ({
    merkmale, lph, gespeichertePruefung, jetztIso,
}) => {
    // Live gerechnet, nicht gespeichert: der Anwender soll beim Setzen eines
    // Hakens sofort sehen, was daraus folgt. Festgeschrieben wird die
    // Pruefung erst beim Speichern (`pruefung` am Kopfsatz).
    // Derselbe Rechenweg wie beim Speichern und in Ampel/Steckbrief — eine
    // Vorschrift, damit Maske und gespeichertes Blatt nicht auseinanderlaufen.
    const pruefung = useMemo(() => schwellenwerteAusMerkmalen(merkmale, jetztIso), [merkmale, jetztIso]);
    // UfsA und A+S-Verdacht haengen nicht am Schnappschuss, sondern an
    // `pflichtenStand` — derselbe Rechenweg wie Hub-Kachel und Steckbrief.
    const weitere = useMemo(
        () => pflichtenStand({ merkmale }, jetztIso).filter(p => p.art === 'ufsa' || p.art === 'asplan'),
        [merkmale, jetztIso],
    );

    const anhang2Treffer = pruefung.anhang2.filter(a => a.zutreffend === true);
    const anhang2Offen = pruefung.anhang2.filter(a => a.zutreffend === undefined);

    return (
        <section className="bg-white rounded-2xl border border-slate-200 p-5">
            <div className="flex items-start justify-between gap-4 mb-3">
                <div>
                    <h3 className="font-bold text-slate-800">Prüfung nach Baustellenverordnung</h3>
                    <p className="text-xs text-slate-500">
                        Rechnet live aus Ihren Angaben. Festgeschrieben wird sie beim Speichern.
                    </p>
                </div>
                <AusgeloestZaehler merkmale={merkmale} lph={lph} />
            </div>

            {pruefung.unvollstaendig && (
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
                    <Info className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                    <span className="text-xs text-amber-800 leading-snug">
                        Das Ergebnis ist <strong>vorläufig</strong>, es fehlen noch Angaben.
                        Ungeklärt ist nicht dasselbe wie ausgeschlossen.
                    </span>
                </div>
            )}

            <PflichtZeile titel="Vorankündigung" stand={pflichtStandAus(pruefung, 'vorankuendigung')} begruendung={pruefung.vorankuendigungBegruendung} />
            <PflichtZeile titel="SiGe-Plan" stand={pflichtStandAus(pruefung, 'sigeplan')} begruendung={pruefung.sigeplanBegruendung} />
            <PflichtZeile titel="Koordinator" stand={pflichtStandAus(pruefung, 'koordinator')} begruendung={pruefung.koordinatorBegruendung} />
            {/* UfsA folgt dem Koordinator, A+S hat zwei Wege (Gebaeudeschadstoffe,
                Boden/Altlasten) — beide live, damit ein Haken bei „Altlastenverdacht"
                sofort sichtbar wird. */}
            {weitere.map(p => (
                <PflichtZeile key={p.art} titel={PFLICHT_ART_LABEL[p.art]} stand={p.stand} begruendung={p.begruendung} />
            ))}

            <div className="mt-4 pt-3 border-t border-slate-100">
                <h4 className="text-xs font-black text-slate-400 uppercase tracking-wider mb-2">
                    Anhang II: besonders gefährliche Arbeiten
                </h4>
                {anhang2Treffer.length === 0 && anhang2Offen.length === 0 && (
                    <p className="text-xs text-slate-500">Keine der zehn Arbeiten kommt vor.</p>
                )}
                {anhang2Treffer.map(a => (
                    <div key={a.nummer} className="flex items-start gap-2 py-1">
                        <span className="shrink-0 w-7 text-center text-[11px] font-black text-rose-700 bg-rose-50 border border-rose-200 rounded px-1 py-0.5">
                            {a.nummer}
                        </span>
                        <div className="min-w-0">
                            <div className="text-xs text-slate-700 leading-snug">{a.bezeichnung}</div>
                            <div className="text-[11px] text-slate-400">{a.begruendung}</div>
                        </div>
                    </div>
                ))}
                {anhang2Offen.length > 0 && (
                    <div className="flex items-start gap-2 mt-2 text-[11px] text-slate-500">
                        <HelpCircle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-amber-500" />
                        <span>
                            Noch ungeklärt: Nr. {anhang2Offen.map(a => a.nummer).join(', ')}. Solange das offen
                            ist, bleiben die zugehörigen Fragen im Katalog.
                        </span>
                    </div>
                )}
            </div>

            {gespeichertePruefung && (
                <div className="mt-4 pt-3 border-t border-slate-100 text-[11px] text-slate-400">
                    Zuletzt festgeschrieben am {String(gespeichertePruefung.berechnet_am || '').slice(0, 10)}.
                </div>
            )}
        </section>
    );
};
